<template>
    <div>
        <v-form ref="step2Form" v-model="step2Valid">
            <!-- Program Application Form section -->
            <div class="program-application-form-section mb-6">
                <div class="d-flex align-center mb-4">
                    <h2 class="text-h6 font-weight-bold mb-0 mr-2">{{ $t('finance.loanPrograms.programApplicationForm') }}</h2>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon color="success" small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <span>{{ $t('finance.loanPrograms.defineAndManageCustomDataFields') }}</span>
                    </v-tooltip>
                </div>
                <v-btn color="primary" class="mb-4" @click="$emit('add-data-field')">
                    <v-icon small left>mdi-form-select</v-icon>
                    {{ $t('finance.loanPrograms.addDataField') }}
                </v-btn>

                <!-- Data fields table -->
                <div class="data-fields-table-wrap" style="border: 1px solid #e0e0e0; border-radius: 4px; overflow: hidden;">
                    <table class="data-fields-table" style="width: 100%; border-collapse: collapse;">
                        <thead>
                            <tr class="data-fields-table-header">
                                <th class="text-left pa-3" style="background: #616161; color: #fff;">
                                    {{ $t('finance.loanPrograms.dataField') }}
                                    <v-icon x-small class="ml-1">mdi-unfold-more-horizontal</v-icon>
                                </th>
                                <th class="text-left pa-3" style="background: #616161; color: #fff; width: 180px;">
                                    {{ $t('finance.loanPrograms.type') }}
                                    <v-icon x-small class="ml-1">mdi-unfold-more-horizontal</v-icon>
                                </th>
                                <th class="text-left pa-3" style="background: #616161; color: #fff; width: 180px;">
                                    {{ $t('finance.loanPrograms.subdata') }}
                                    <v-icon x-small class="ml-1">mdi-unfold-more-horizontal</v-icon>
                                </th>
                                <th class="text-left pa-3" style="background: #616161; color: #fff; width: 140px;">
                                    {{ $t('finance.loanPrograms.mandatory') }}
                                    <v-icon x-small class="ml-1">mdi-unfold-more-horizontal</v-icon>
                                </th>
                                <th class="text-right pa-3" style="background: #616161; color: #fff; width: 120px;"></th>
                            </tr>
                        </thead>
                        <tbody v-for="(field, index) in dataFields" :key="field._uid || `top-${index}`">
                            <tr
                                class="data-fields-table-row"
                                :class="{ 'group-row': field.isGroup }"
                                
                            >
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">
                                    <span :style="field.isGroup ? '' : 'padding-left: 0'">
                                        {{ field.isGroup ? field.groupTitle : field.fieldTitle }}
                                    </span>
                                </td>
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">
                                    {{ field.isGroup ? $t('finance.loanPrograms.group') : getDataTypeLabel(field.dataType) }}
                                </td>
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">
                                    {{ field.isGroup ? (field.groupFields && field.groupFields.length) || 0 : 0 }}
                                </td>
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">
                                    <span class="mr-2">{{ field.isMandetory ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                                    <v-switch
                                        :input-value="field.isMandetory"
                                        @change="toggleMandatory(index, $event)"
                                        hide-details
                                        color="primary"
                                        inset
                                        dense
                                        class="d-inline-block mt-0"
                                        style="max-width: 40px;"
                                    ></v-switch>
                                </td>
                                <td class="pa-3 text-right d-flex justify-end" style="border-bottom: 1px solid #e0e0e0;">
                                    <template v-if="field.isGroup">
                                        <v-btn
                                            icon
                                            @click="toggleGroupExpand(field)"
                                        >
                                            <v-icon>{{ isGroupExpanded(field) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-btn class="mr-1" icon style="width: 30px; height: 30px; background-color: #0EBF67;" color="white" @click="$emit('edit-data-field', index)">
                                        <v-icon style="font-size: 18px;">mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn class="mr-1" icon style="width: 30px; height: 30px; background-color: #DA3D3D;" color="white" @click="$emit('remove-data-field', index)">
                                        <v-icon style="font-size: 18px;">mdi-delete-outline</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                            <!-- Nested rows (group fields) when expanded -->
                            <tr
                                v-for="(subField, subIndex) in (field.isGroup && isGroupExpanded(field) && field.groupFields ? field.groupFields : [])"
                                :key="`nested-${field._uid || index}-${subIndex}`"
                                class="data-fields-table-row nested-row"
                            >
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0; padding-left: 40px !important;">
                                    {{ subField.fieldTitle }}
                                </td>
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">
                                    {{ getDataTypeLabel(subField.dataType) }}
                                </td>
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">0</td>
                                <td class="pa-3" style="border-bottom: 1px solid #e0e0e0;">
                                    <span class="mr-2">{{ subField.isMandetory ? $t('finance.loanPrograms.yes') : $t('finance.loanPrograms.no') }}</span>
                                    <v-switch
                                        :input-value="subField.isMandetory"
                                        @change="toggleNestedMandatory(index, subIndex, $event)"
                                        hide-details
                                        color="primary"
                                        inset
                                        dense
                                        class="d-inline-block mt-0"
                                        style="max-width: 40px;"
                                    ></v-switch>
                                </td>
                                <td class="pa-3 text-right d-flex justify-end" style="border-bottom: 1px solid #e0e0e0;">
                                    <v-btn class="mr-1" icon style="width: 30px; height: 30px; background-color: #0EBF67;" color="white" @click="$emit('edit-data-field', index)">
                                        <v-icon style="font-size: 18px;">mdi-pencil</v-icon>
                                    </v-btn>
                                    <v-btn class="mr-1" icon style="width: 30px; height: 30px; background-color: #DA3D3D;" color="white" @click="$emit('remove-group-field', index, subIndex)">
                                        <v-icon style="font-size: 18px;">mdi-delete-outline</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-if="!dataFields || !dataFields.length">
                            <tr>
                                <td colspan="5" class="pa-4 text-center grey--text">
                                    {{ $t('finance.loanPrograms.noDataFieldsYet') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </v-form>
    </div>
</template>

<script>
export default {
    name: 'StepTwo',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        dataFields: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            step2Valid: false,
            expandedGroups: {}, // { [topLevelIndex]: true }
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(v) {
                this.step2Valid = v;
            },
        },
        step2Valid(v) {
            this.$emit('input', v);
        },
    },
    methods: {
        validate() {
            return this.$refs.step2Form ? this.$refs.step2Form.validate() : false;
        },
        getDataTypeLabel(dataType) {
            const dataTypeMap = {
                text_field: this.$t('finance.loanPrograms.textField'),
                text_area: this.$t('finance.loanPrograms.textArea'),
                text_field_numeric: this.$t('finance.loanPrograms.textFieldNumeric'),
                radio_button: this.$t('finance.loanPrograms.radioButton'),
                check_boxes: this.$t('finance.loanPrograms.checkBoxes'),
                dropdown: this.$t('finance.loanPrograms.dropdown'),
                measurement_unit: this.$t('finance.loanPrograms.measurementUnit'),
                attach_file: this.$t('finance.loanPrograms.attachFile'),
                digital_signature: this.$t('finance.loanPrograms.digitalSignatureLabel'),
                data_list: this.$t('finance.loanPrograms.dataList'),
                date_field: this.$t('finance.loanPrograms.date'),
                date: this.$t('finance.loanPrograms.date'),
            };
            return dataTypeMap[dataType] || dataType || '';
        },
        isGroupExpanded(field) {
            const key = field._uid || field.groupTitle || field.fieldTitle;
            return !!this.expandedGroups[key];
        },
        toggleGroupExpand(field) {
            const key = field._uid || field.groupTitle || field.fieldTitle;
            this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
        },
        toggleMandatory(index, value) {
            if (!this.dataFields[index]) return;
            this.$set(this.dataFields[index], 'isMandetory', !!value);
        },
        toggleNestedMandatory(parentIndex, childIndex, value) {
            const group = this.dataFields[parentIndex];
            if (!group || !group.groupFields || !group.groupFields[childIndex]) return;
            this.$set(group.groupFields[childIndex], 'isMandetory', !!value);
        },
    },
};
</script>

<style scoped>
.data-fields-table-header th {
    font-weight: 600;
    font-size: 0.875rem;
}
.data-fields-table-row {
    background: #fff;
}
.data-fields-table-row.group-row {
    background: #f5f5f5;
}
.data-fields-table-row.nested-row {
    background: #fafafa;
}
.data-fields-table-row:hover {
    background: #f9f9f9;
}
.data-fields-table-wrap {
    background: #fff;
}
</style>
